// src/components/preAuth/LoginScreen.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, LogIn, AlertCircle, RefreshCw, Sun, Moon, ShieldCheck } from 'lucide-react';
import {
    signInWithEmailAndPassword,
    getMultiFactorResolver,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    RecaptchaVerifier
} from 'firebase/auth';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { auth } from '../../lib/firebase.js';
import { PALETTE, THEME_CLASSES } from '../../lib/theme.js';
import { CONFIG } from '../../lib/config.js';
import rapidLogo from '../../assets/rapidLogo.png';
import { useAuth } from '../../context/AuthContext.jsx';

const loginSchema = z.object({
    email: z.string().email("Please enter a valid email address."),
    password: z.string().min(1, "Password is required.")
});

const mfaSchema = z.object({
    smsCode: z.string().length(6, "Verification code must be exactly 6 digits.").regex(/^\d+$/, "Code must contain only numbers.")
});

const cleanError = (err) => {
    if (!err) return "An unknown error occurred.";
    switch (err.code) {
        case 'auth/invalid-credential':
        case 'auth/user-not-found':
        case 'auth/wrong-password':
            return "Invalid email or password. Please try again.";
        case 'auth/too-many-requests':
            return "Access temporarily locked due to multiple failed attempts. Try again later.";
        case 'auth/user-disabled':
            return "This account has been suspended. Please contact support.";
        case 'auth/email-already-in-use':
            return "An account with this email address already exists.";
        case 'auth/weak-password':
            return "Your password must be at least 8 characters long.";
        case 'auth/network-request-failed':
            return "Network connection failed. Please check your internet connection.";
        default:
            if (err.message) {
                return err.message.replace(/Firebase:\s*/gi, '').replace(/\s*\(auth\/[a-zA-Z0-9-]+\)\.?/gi, '').trim();
            }
            return "An unexpected authentication error occurred.";
    }
};

export default function LoginScreen({ isDark, toggleTheme }) {
    const navigate = useNavigate();
    const { login, startProvisioning } = useAuth();

    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(null);

    const [mfaRequired, setMfaRequired] = useState(false);
    const [mfaResolver, setMfaResolver] = useState(null);
    const [verificationId, setVerificationId] = useState('');
    const [isSmsSent, setIsSmsSent] = useState(false);
    const [suspendedInfo, setSuspendedInfo] = useState(null);

    const recaptchaVerifierRef = useRef(null);

    const { register: registerLogin, handleSubmit: handleLoginSubmit, formState: { errors: loginErrors } } = useForm({
        resolver: zodResolver(loginSchema)
    });

    const { register: registerMfa, handleSubmit: handleMfaSubmit, reset: resetMfa, watch: watchMfa, formState: { errors: mfaErrors } } = useForm({
        resolver: zodResolver(mfaSchema)
    });

    const smsCodeValue = watchMfa('smsCode');

    useEffect(() => {
        return () => {
            if (recaptchaVerifierRef.current) {
                try { recaptchaVerifierRef.current.clear(); } catch(e) {}
            }
        };
    }, []);

    const processBackendAuth = async (firebaseUser) => {
        const token = await firebaseUser.getIdToken(true);
        const response = await fetch(`${CONFIG.API_BASE_URL}/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });

        const rawText = await response.text();
        if (!response.ok) {
            let errorMsg = "Login Failed";
            try {
                const errData = JSON.parse(rawText);
                errorMsg = errData.error || errData.message || errorMsg;
            } catch (e) {
                errorMsg = `Server Error (${response.status}): Endpoint not found or unavailable.`;
            }
            throw new Error(errorMsg);
        }

        const data = JSON.parse(rawText);
        if (data.status === 'suspended' && data.checkout_url) {
            setLoading(false);
            setSuspendedInfo({ message: data.message || "Trial expired. Redirecting to billing...", url: data.checkout_url });
            return;
        }

        if (data.status === 'requires_tos') {
            navigate('/accept-terms', { state: { loginData: data } });
            return;
        }

        if (data.status === 'pending_provision') {
            startProvisioning(data.token, data.tenant_id, data.user?.email, data.user, data.status, data.trial_end_date);
            navigate('/provisioning');
        } else {
            login(data.token, data.tenant_id, data.user?.email, data.user, data.status, data.trial_end_date);
            navigate('/');
        }
    };

    const onLogin = async (data) => {
        setLoading(true);
        setApiError(null);
        try {
            const userCredential = await signInWithEmailAndPassword(auth, data.email.toLowerCase(), data.password);
            await processBackendAuth(userCredential.user);
        } catch (err) {
            if (err.code === 'auth/multi-factor-auth-required') {
                const resolver = getMultiFactorResolver(auth, err);
                setMfaResolver(resolver);
                setMfaRequired(true);
                await sendSmsCode(resolver);
            } else {
                setApiError(cleanError(err) || "Failed to log in. Please check your credentials.");
                setLoading(false);
            }
        }
    };

    const sendSmsCode = async (resolver) => {
        try {
            if (recaptchaVerifierRef.current) {
                try { recaptchaVerifierRef.current.clear(); } catch(e) {}
                recaptchaVerifierRef.current = null;
            }

            recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-login', {
                size: 'invisible'
            });

            const phoneHint = resolver.hints.find(hint => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
            if (!phoneHint) throw new Error("No SMS factor found on this account.");

            const phoneInfoOptions = {
                multiFactorHint: phoneHint,
                session: resolver.session
            };

            const phoneAuthProvider = new PhoneAuthProvider(auth);
            const vId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifierRef.current);
            setVerificationId(vId);
            setIsSmsSent(true);
            setLoading(false);
        } catch (err) {
            setApiError(cleanError(err) || "Failed to send SMS code. Please try again later.");
            setLoading(false);
            if (recaptchaVerifierRef.current) {
                try { recaptchaVerifierRef.current.clear(); } catch(e) {}
                recaptchaVerifierRef.current = null;
            }
        }
    };

    const onMfaSubmit = async (data) => {
        setApiError(null);
        setLoading(true);
        try {
            const cred = PhoneAuthProvider.credential(verificationId, data.smsCode);
            const assertion = PhoneMultiFactorGenerator.assertion(cred);
            const userCredential = await mfaResolver.resolveSignIn(assertion);
            await processBackendAuth(userCredential.user);
        } catch (err) {
            setApiError(cleanError(err) || "Invalid verification code. Please try again.");
            setLoading(false);
        }
    };

    return (
        <div className={`min-h-screen w-full flex items-center justify-center p-6 relative overflow-hidden transition-colors duration-500 ${THEME_CLASSES.bgMain(isDark)} ${THEME_CLASSES.textPrimary(isDark)}`} style={{ fontFamily: PALETTE.font }}>
            <div id="recaptcha-login"></div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[800px] h-[800px] rounded-full blur-[120px] opacity-20 bg-gradient-to-tr" style={{ backgroundImage: `linear-gradient(to top right, ${PALETTE.blue}, ${isDark ? PALETTE.blue2 : PALETTE.greenLight})` }}></div>
            </div>

            {toggleTheme && (
                <button onClick={toggleTheme} className={`absolute top-6 right-6 w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md transition-all z-20 border ${THEME_CLASSES.bgHighlight(isDark)} ${THEME_CLASSES.borderSubtle(isDark)} ${THEME_CLASSES.textPrimary(isDark)} ${THEME_CLASSES.bgHover(isDark)}`}>
                    {isDark ? <Sun size={18} /> : <Moon size={18} />}
                </button>
            )}

            <div className={`relative z-10 w-full max-w-[480px] backdrop-blur-2xl rounded-[32px] p-8 sm:p-12 shadow-2xl transition-all border ${THEME_CLASSES.bgCard(isDark)} ${THEME_CLASSES.borderSubtle(isDark)}`}>
                <div className="flex flex-col items-center text-center mb-10">
                    <img src={rapidLogo} alt="Rapid Logo" className="w-16 h-16 object-contain mb-6" />
                    <h1 className="text-3xl font-bold tracking-tight mb-3">
                        {mfaRequired ? "Two-Factor Auth" : "Welcome Back"}
                    </h1>
                    <p className={`text-sm ${THEME_CLASSES.textSecondary(isDark)}`}>
                        {mfaRequired ? "Enter the 6-digit code we sent via SMS." : "Sign in to your Rapid Command Center."}
                    </p>
                </div>

                {apiError && (
                    <div className={`mb-6 flex items-start space-x-2 text-xs font-medium p-3 rounded-lg border ${isDark ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-red-50 text-red-600 border-red-200'}`}>
                        <AlertCircle size={16} className="mt-0.5 shrink-0" />
                        <span>{apiError}</span>
                    </div>
                )}

                {!mfaRequired ? (
                    <form onSubmit={handleLoginSubmit(onLogin)} className="space-y-6">
                        <div>
                            <div className="relative group">
                                <Mail className={`absolute left-4 top-3.5 z-10 ${THEME_CLASSES.textSecondary(isDark)}`} size={18} />
                                <input
                                    type="email" placeholder="Email Address"
                                    {...registerLogin('email')}
                                    className={`${THEME_CLASSES.input(isDark)} pl-11 py-3 w-full`}
                                />
                            </div>
                            {loginErrors.email && <p className="text-red-500 text-xs font-bold mt-1 ml-1">{loginErrors.email.message}</p>}
                        </div>

                        <div>
                            <div className="relative group">
                                <Lock className={`absolute left-4 top-3.5 z-10 ${THEME_CLASSES.textSecondary(isDark)}`} size={18} />
                                <input
                                    type="password" placeholder="Password"
                                    {...registerLogin('password')}
                                    className={`${THEME_CLASSES.input(isDark)} pl-11 py-3 w-full`}
                                />
                            </div>
                            {loginErrors.password && <p className="text-red-500 text-xs font-bold mt-1 ml-1">{loginErrors.password.message}</p>}
                        </div>

                        <button
                            type="submit" disabled={loading}
                            className="w-full h-12 rounded-2xl font-bold text-white shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center space-x-2 mt-4 disabled:opacity-50"
                            style={{ backgroundColor: PALETTE.blue, boxShadow: `0 8px 20px -6px ${PALETTE.blue}50` }}
                        >
                            {loading ? <RefreshCw className="animate-spin" size={20} /> : <><span>Sign In</span><LogIn size={18} /></>}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleMfaSubmit(onMfaSubmit)} className="space-y-6">
                        {isSmsSent ? (
                            <div>
                                <div className="relative">
                                    <ShieldCheck className={`absolute left-4 top-3.5 z-10 ${THEME_CLASSES.textSecondary(isDark)}`} size={18} />
                                    <input
                                        type="text" autoFocus
                                        {...registerMfa('smsCode')}
                                        className={`${THEME_CLASSES.input(isDark)} pl-11 py-3 w-full text-center tracking-[0.5em] font-mono text-xl`}
                                        placeholder="000000"
                                    />
                                </div>
                                {mfaErrors.smsCode && <p className="text-red-500 text-xs font-bold mt-1 text-center">{mfaErrors.smsCode.message}</p>}
                            </div>
                        ) : (
                            <div className="flex justify-center p-4">
                                <RefreshCw size={24} className="animate-spin text-blue-500" />
                            </div>
                        )}
                        <div className="flex space-x-3 mt-4">
                            <button
                                type="button" onClick={() => { setMfaRequired(false); resetMfa(); setApiError(null); }}
                                className={`px-6 h-12 rounded-2xl font-bold transition-all border ${THEME_CLASSES.bgHighlight(isDark)} ${THEME_CLASSES.borderSubtle(isDark)} ${THEME_CLASSES.textPrimary(isDark)}`}
                            >
                                Back
                            </button>
                            <button
                                type="submit" disabled={loading || !isSmsSent || (smsCodeValue && smsCodeValue.length !== 6)}
                                className="flex-1 h-12 rounded-2xl font-bold text-white shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center space-x-2 disabled:opacity-50"
                                style={{ backgroundColor: PALETTE.blue, boxShadow: `0 8px 20px -6px ${PALETTE.blue}50` }}
                            >
                                {loading ? <RefreshCw className="animate-spin" size={20} /> : <span>Verify Code</span>}
                            </button>
                        </div>
                    </form>
                )}

                {!mfaRequired && (
                    <div className="mt-8 text-center">
                        <p className={`text-sm font-medium ${THEME_CLASSES.textSecondary(isDark)}`}>
                            Don't have an account?
                            <button onClick={() => navigate('/signup')} className="font-bold hover:underline ml-1 transition-colors" style={{ color: PALETTE.blue }}>
                                Create Workspace
                            </button>
                        </p>
                    </div>
                )}
            </div>

            {suspendedInfo && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className={`w-full max-w-sm p-6 rounded-3xl shadow-2xl border text-center ${THEME_CLASSES.bgCard(isDark)} ${THEME_CLASSES.borderSubtle(isDark)} animate-in zoom-in-95 duration-200`}>
                        <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 mx-auto mb-4 shrink-0">
                            <AlertCircle size={28} />
                        </div>
                        <h3 className={`text-lg font-bold mb-2 ${THEME_CLASSES.textPrimary(isDark)}`}>Trial Expired</h3>
                        <p className={`text-xs mb-6 ${THEME_CLASSES.textSecondary(isDark)}`}>
                            {suspendedInfo.message}
                        </p>
                        <button
                            onClick={() => { window.location.href = suspendedInfo.url; }}
                            className="w-full py-3 rounded-2xl font-bold text-white shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
                            style={{ backgroundColor: PALETTE.blue }}
                        >
                            Proceed to Billing
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}